import axios from "axios";
import { store } from "./store";
import dayjs from "dayjs";
import JWTDecoder from "../utils/utils";
import { authTypes } from "./auth/auth.types";
import { apiBaseUrl } from "../config";
import { SOCKET_IO_SERVER } from "../socket/config";

// Base HTTP client for public API calls
const http = axios.create({
  baseURL: apiBaseUrl,
  timeout: 45000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Private HTTP client for authenticated API calls
const privateHttp = axios.create({
  baseURL: apiBaseUrl,
  timeout: 45000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Socket HTTP client for socket-related API calls
const socketHttp = axios.create({
  baseURL: `${SOCKET_IO_SERVER}/`,
  timeout: 45000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => (error ? prom.reject(error) : prom.resolve(token)));
  failedQueue = [];
};

// Retrieve tokens from Redux or localStorage
const getTokens = () => {
  const state = store.getState().auth;
  return {
    accessToken: state.accessToken || localStorage.getItem("accessToken"),
    refreshToken: state.refreshToken || localStorage.getItem("refreshToken"),
  };
};

// Save tokens to Redux and localStorage
const saveTokens = (accessToken, refreshToken, expiresIn = 3540) => {
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  const tokenExpiration = Math.floor(Date.now() / 1000) + expiresIn;

  store.dispatch({
    type: authTypes.REFRESH_USER_TOKEN,
    payload: { token: accessToken, refreshToken, tokenExpiration },
  });

  return { accessToken, refreshToken, tokenExpiration };
};

// Check if token is expired (within 30 seconds of expiry)
const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const decodedToken = JWTDecoder.decode(token);
    return dayjs.unix(decodedToken.exp).diff(dayjs()) < 30000;
  } catch (error) {
    console.error("Token validation error:", error);
    return true;
  }
};

// Log out user and clear tokens
const logoutUser = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  store.dispatch({ type: authTypes.LOGOUT });
};

// Refresh access token using refresh token
const refreshAccessToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }));
  }

  isRefreshing = true;
  store.dispatch({ type: authTypes.REFRESH_USER_TOKEN_START });

  try {
    const { refreshToken } = getTokens();
    if (!refreshToken) throw new Error("No refresh token available");

    const response = await http.post("/auth/refreshToken", { refreshToken });
    const { token, refreshToken: newRefreshToken } = response.data;
    if (!token || !newRefreshToken) throw new Error("Invalid refresh token response");

    const tokens = saveTokens(token, newRefreshToken);
    privateHttp.defaults.headers.common.Authorization = `Bearer ${token}`;
    socketHttp.defaults.headers.common.Authorization = `Bearer ${token}`;

    processQueue(null, token);
    isRefreshing = false;
    return tokens.accessToken;
  } catch (error) {
    console.error("Token refresh failed:", error);
    processQueue(error);
    logoutUser();
    isRefreshing = false;
    throw error;
  }
};

// Request interceptor for privateHttp
privateHttp.interceptors.request.use(
  async (config) => {
    const { accessToken } = getTokens();
    if (!accessToken) return config;

    if (isTokenExpired(accessToken)) {
      try {
        const newToken = await refreshAccessToken();
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (error) {
        console.error("Failed to refresh token for request:", error);
      }
    } else {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for privateHttp
privateHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return privateHttp(originalRequest);
      } catch (refreshError) {
        console.error("Failed to retry request after refresh:", refreshError);
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Request interceptor for socketHttp
socketHttp.interceptors.request.use(
  async (config) => {
    const { accessToken } = getTokens();
    if (!accessToken) return config;

    if (isTokenExpired(accessToken)) {
      try {
        const newToken = await refreshAccessToken();
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (error) {
        console.error("Socket request refresh error:", error);
      }
    } else {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for socketHttp
socketHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return socketHttp(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Public API endpoints
export const login = (payload) => http.post("/login", payload);
export const appleLogin = (payload) => http.post("/login/apple-oauth", payload);
export const signUp = (payload) => http.post("/register", payload);
export const googleLogin = (payload) => http.post("/login/google-oauth", payload);
export const loginWithPhoneNumber = (payload) => http.post("/login/phone", payload)
export const refresh = (item) => http.post("/auth/refreshToken", item);
export const joinAmbassadorProgram = (payload) => http.post("/ambassador", payload);
export const searchEvents = ({ page, search }) => http.get(`/events?page=${page}&q=${search}`);
export const verifyMail = (payload) => http.post(`/auth/verifyEmail`, payload)
export const resetPassword = (payload) =>http.post(`/auth/password-reset`, payload)
export const verifyOTP = (payload)=>http.post("/login/phone/verify", payload);

// Private API endpoints - User and Profile
export const getUserPosts = () => privateHttp.get(`/events`);
export const getUserFlaggedPosts = () => privateHttp.get(`/events/flagged`);
export const getAllUserPost = ({ more, lastDoc }) =>
  privateHttp.get(`/events/${!more ? "null" : lastDoc}/pages`);
export const getRefreshPost = () => privateHttp.get(`/events`);
export const getAllPublicEvent = ({ page, lat, lng }) =>
  privateHttp.get(`/public-events?lat=${lat}&lng=${lng}&page=${page}&limit=20`);
export const getSinglePublicEvent = (id) => privateHttp.get(`/public-events/${id}`);
export const updateProfile = (username, payload) =>
  privateHttp.post(`/user/${username}/update-profile`, payload);
export const updateProfileImage = (payload) => privateHttp.post(`/changePicture`, payload);
export const getUserDetails = () => privateHttp.get(`/users`);
export const getAllUser = () => privateHttp.get(`/users/all`);
export const updateLocation = (payload) => privateHttp.patch(`/user/location`, payload);
export const deleteAccount = (item) => privateHttp.post("/deleteAccount", item);

// Private API endpoints - Events and Interactions
export const likeEventById = (eventId, payload) => privateHttp.post(`/events/${eventId}/like`, payload);
export const userUploadPost = (payload) => privateHttp.post(`/events`, payload);
export const postEvent = (payload) => privateHttp.post(`/public-events`, payload);
export const updateEvent = (id) => privateHttp.patch(`/public-events/${id}/interest`);
export const reportPost = (payload) => privateHttp.post(`/reportPost`, payload);
export const userPostCommentStatus = (eventId, payload) =>
  privateHttp.patch(`event/${eventId}/status`, payload);
export const getMapEvent = (payload) =>
  privateHttp.get(`/mapEvent?lat=${payload?.latitude}&lon=${payload?.longitude}`);
export const userPost = (username) => privateHttp.get(`/events/client/${username}`);
export const trendingPost = () => privateHttp.get(`/events/hashtags`);
export const getSingleActivities = (eventId) => privateHttp.get(`${apiBaseUrl}/events/${eventId}`);

// Private API endpoints - Notifications and Activities
export const getAllNotifications = ({ more, newDoc }) =>
  privateHttp.get(`/notification/${!more ? "null" : newDoc}`);
export const getAllActivityAction = ({ getIndex, newDoc }) =>
  privateHttp.get(`/alertActivity/${getIndex === 1 ? "null" : newDoc}`);
export const getNotificationsCount = () => privateHttp.get(`/notifications/count`);
export const getActivities = (item) => privateHttp.get(`${apiBaseUrl}/notification/${item}`);

// Private API endpoints - Livestreams and Communities
export const getAllUserLivestreams = () => privateHttp.get(`/liveStream/${null}/user`);
export const allCommunities = (id) => privateHttp.get(`/communities${id ? `?lastDocId=${id}` : ""}`);
export const allCommunityFeeds = () => privateHttp.get(`/events/community`);
export const communityById = (id) => privateHttp.get(`/communities/${id}`);
export const leaveCommunity = (id) => privateHttp.post(`/communities/${id}/leave`);
export const joinCommunity = (id) => privateHttp.post(`/communities/${id}/join`);
export const inviteCommunity = (id, data) => privateHttp.post(`/communities/${id}/invite`, data);
export const createACommunity = (data) => privateHttp.post(`/communities`, data);
export const communityPost = (id) => privateHttp.get(`/events/community?communityID=${id}`);
export const searchCommunityFeeds = (searchTerm, page) =>
  privateHttp.get(`events/search/communities?searchTerm=${searchTerm.toLowerCase()}&page=${page}`);
export const searchCommunity = (name, page) =>
  privateHttp.get(`/communities/search?searchTerm=${name}&page=${page}`);

// Socket API endpoints
export const getLiveRoomDetail = (roomId) => socketHttp.get(`/live/${roomId}`);
export const postMessage = (payload) => socketHttp.post("/message", payload);

// Export token management utilities
export { refreshAccessToken, getTokens, saveTokens, isTokenExpired };